import ColumnSelector from "@components/Commons/ColumnSelector/ColumnSelector";
import useSelectedColumns from "@lib/hooks/use-selected-columns";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "@components/ui/table";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@tracify/ui/card";

import React, { useCallback, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { useIsMobile } from "@tracify/ui";
import { BarChart2, ChevronDown, Table2 } from "lucide-react";
import {
  useAttributionPresetState,
  useDateRangeState,
  useNewVsReturningState,
} from "@lib/hooks/url-state";
import {
  FilterTypesEnum,
  type AdsFilterInterface,
  AVAILABLE_MARKETING_CHANNEL_OPTIONS,
} from "@tracify/shared";
import { formatFilterToQuery } from "@lib/util-functions/table/filters";
import MetricNameWithTooltip from "@components/MetricNameWithTooltip/MetricNameWithTooltip";
import SpendTableDisplay from "../../NumberDisplays/SpendTableDisplay";
import RevenueTableDisplay from "../../NumberDisplays/RevenueTableDisplay";
import OrdersTableDisplay from "../../NumberDisplays/OrdersTableDisplay";
import RoasTableDisplay from "../../NumberDisplays/RoasTableDisplay";
import CacTableDisplay from "../../NumberDisplays/CacTableDisplay";
import CpoTableDisplay from "../../NumberDisplays/CpoTableDisplay";
import AovTableDisplay from "../../NumberDisplays/AovTableDisplay";
import ConversionRateTableDisplay from "../../NumberDisplays/ConversionRateTableDisplay";
import OptionalCurrencyTableDisplay from "../../NumberDisplays/OptionalCurrencyTableDisplay";
import AtcTableDisplay from "../../NumberDisplays/AtcTableDisplay";
import ProductviewTableDisplay from "../../NumberDisplays/ProductviewTableDisplay";
import PageviewTableDisplay from "../../NumberDisplays/PageviewTableDisplay";
import NewCustomerRateTableDisplay from "../../NumberDisplays/NewCustomerRateTableDisplay";
import NewVisitorRateTableDisplay from "../../NumberDisplays/NewVisitorRateTableDisplay";
import {
  CONSTANT_LABELS,
  DEFAULT_SORTING,
  getMetricLabelForDashboardMode,
  mapChannelToColor,
} from "@/constants/constants";
import { useChannelColorMap } from "@lib/hooks/use-channel-color-map";
import { useChannelNameMap } from "@lib/hooks/use-channel-name-map";
import { capitalizeFirstLetter } from "@lib/util-functions/string";
import { dashboardModeAtom } from "@/atoms";
import { useAtomValue } from "jotai";
import { usePresets } from "@lib/api-hooks/usePresets/usePresets";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import DirectTooltip from "./DirectTooltip";
import OrganicTooltip from "./OrganicTooltip";
import dayjs from "dayjs";
import { DailyMetric, FixedKpiMetric, KpiMetric } from "@lib/api-hooks";
import { KpisPerChannelResponse } from "@lib/api-hooks/useKpisPerChannel";
import HistoricalChartDisplay from "@components/NumberDisplays/HistoricalChartDisplay";
import HistoricalChartHeaderSelect from "@components/NumberDisplays/HistoricalChartHeaderSelect";
import { ChannelOverviewExportMenu } from "@components/AdsTable/ChannelOverviewExportMenu";
import { gatewayTimeoutMessage } from "@lib/api-hooks/utils/error-handling";
import axios from "axios";
import { Granularity } from "@lib/api-hooks/useKpiBreakdownForChannel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tracify/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@tracify/ui/popover";
import { Checkbox } from "@tracify/ui/checkbox";
import {
  MarketingChannelOverviewChart,
  MarketingChannelOverviewChartSkeleton,
} from "./MarketingChannelOverviewChart";
import {
  CHANNEL_OVERVIEW_CHART_METRICS,
  type ChannelOverviewChartMetric,
  getSortedGranularityChannelData,
} from "./marketing-channel-overview-chart-utils";
import { disabledInfluencerHistoricalChartMetrics } from "@lib/hooks/constants";
import { buildPathWithSearchParams } from "@lib/navigation/url-search-params";
import {
  type AcquisitionRoasMetric,
  getAcquisitionRoas,
} from "@lib/util-functions/acquisition-roas";
import { getCostPerView } from "@lib/util-functions/cost-per-view";
type MarketingChannelOverviewTableProps = {
  data?: KpisPerChannelResponse;
  comparedData?: KpisPerChannelResponse;
  error: Error | null;
  loading?: boolean;
  isInfluencerData?: boolean;
  title?: string;
  filters?: AdsFilterInterface[];
  granularity?: Granularity;
};

type TableType = FixedKpiMetric &
  KpiMetric & {
    cost_per_view?: number;
    historical: DailyMetric[];
    channel: string;
    compared?: FixedKpiMetric &
      KpiMetric &
      AcquisitionRoasMetric & {
        cost_per_view?: number;
        historical: DailyMetric[];
      };
  } & AcquisitionRoasMetric;

const columnHelper = createColumnHelper<TableType>();

function MarketingChannelOverviewTable(
  props: MarketingChannelOverviewTableProps
) {
  const {
    data,
    error,
    loading,
    isInfluencerData,
    title,
    comparedData,
    filters,
    granularity = "daily",
  } = props;
  const router = useRouter();
  const { selectedOrganisation } = useSelectedStores();
  const { compared, startTime, endTime } = useDateRangeState();
  const channelColorMap = useChannelColorMap();
  const channelNameMap = useChannelNameMap();
  const { attributionPreset } = useAttributionPresetState();
  const [view, setView] = useState<"table" | "chart">("table");
  const [chartMetric, setChartMetric] =
    useState<ChannelOverviewChartMetric>("customer_revenue");
  const [hiddenChannels, setHiddenChannels] = useState<Set<string>>(
    () => new Set()
  );
  const dashboardMode = useAtomValue(dashboardModeAtom);
  const { data: presets } = usePresets(selectedOrganisation ?? "", {
    enabled: !!selectedOrganisation,
  });
  const { selectedColumns, visibleColumns } = useSelectedColumns({
    table: isInfluencerData
      ? "influencerChannelOverview"
      : "marketingChannelOverview",
  });
  const selectedTimerange = useMemo(
    () => dayjs(endTime).diff(dayjs(startTime), "days"),
    [endTime, startTime]
  );

  const { newVsReturning: nvrKey } = useNewVsReturningState();
  const isMobile = useIsMobile();
  const canShowChart = !isInfluencerData;
  const activeView = canShowChart ? view : "table";

  const tableData = useMemo(() => {
    if (compared) {
      const newData: TableType[] = [];
      for (const [channel, channelData] of Object.entries(
        data?.channels ?? {}
      )) {
        const compared = comparedData?.channels[channel];
        newData.push({
          channel: channel,
          ...channelData.overall[nvrKey],
          ...channelData.overall.fixed,
          cost_per_view: getCostPerView(channelData.overall.fixed),
          acquisition_return_on_ad_spend: getAcquisitionRoas(
            channelData.overall
          ),
          historical: getSortedGranularityChannelData(channelData, granularity),
          compared: compared
            ? {
                ...compared.overall[nvrKey],
                ...compared.overall.fixed,
                cost_per_view: getCostPerView(compared.overall.fixed),
                acquisition_return_on_ad_spend: getAcquisitionRoas(
                  compared.overall
                ),
                historical: getSortedGranularityChannelData(
                  compared,
                  granularity
                ),
              }
            : undefined,
        });
      }
      return newData;
    } else
      return Object.entries(data?.channels ?? {}).map(
        ([channel, channelData]) => ({
          channel: channel,
          ...channelData.overall[nvrKey],
          ...channelData.overall.fixed,
          cost_per_view: getCostPerView(channelData.overall.fixed),
          acquisition_return_on_ad_spend: getAcquisitionRoas(
            channelData.overall
          ),
          historical: getSortedGranularityChannelData(channelData, granularity),
        })
      ) as TableType[];
  }, [compared, data, comparedData, nvrKey, granularity]);

  const chartChannels = useMemo(
    () =>
      Object.keys(data?.channels ?? {}).sort((a, b) =>
        (channelNameMap[a] ?? CONSTANT_LABELS[a] ?? a).localeCompare(
          channelNameMap[b] ?? CONSTANT_LABELS[b] ?? b
        )
      ),
    [channelNameMap, data?.channels]
  );

  const visibleChartChannels = useMemo(
    () => chartChannels.filter((channel) => !hiddenChannels.has(channel)),
    [chartChannels, hiddenChannels]
  );

  const toggleChartChannel = useCallback((channel: string) => {
    setHiddenChannels((prev) => {
      const next = new Set(prev);
      if (next.has(channel)) {
        next.delete(channel);
      } else {
        next.add(channel);
      }
      return next;
    });
  }, []);

  const allChartChannelsVisible =
    chartChannels.length > 0 &&
    visibleChartChannels.length === chartChannels.length;

  const toggleAllChartChannels = useCallback(() => {
    setHiddenChannels(
      allChartChannelsVisible ? new Set(chartChannels) : new Set()
    );
  }, [allChartChannelsVisible, chartChannels]);

  const hiddenChartChannelCount =
    chartChannels.length - visibleChartChannels.length;

  const chartMetricOptions = useMemo(
    () =>
      CHANNEL_OVERVIEW_CHART_METRICS.map((option) => ({
        ...option,
        label: getMetricLabelForDashboardMode(option.value, dashboardMode),
      })),
    [dashboardMode]
  );

  const pinnedColumns = { left: ["channel"] };
  const preset = presets?.find((p) => p.presetId === attributionPreset);
  const allChannelsSelected =
    preset?.touchpoints.length === AVAILABLE_MARKETING_CHANNEL_OPTIONS.length ||
    preset?.touchpoints.includes("all_touchpoints");

  const columns = useMemo(
    () => [
      columnHelper.accessor("channel", {
        enableResizing: false,
        sortDescFirst: false,
        header: "Channel",
        size: isMobile ? 120 : 150,
        cell: ({ row, getValue }) => {
          const channelValue = getValue() as string;
          const channelName =
            (!isInfluencerData ? channelNameMap[channelValue] : undefined) ??
            CONSTANT_LABELS[channelValue];
          const handleNavigateToInfluencers = () => {
            if (!isInfluencerData || !channelValue) return;
            const filterStr = formatFilterToQuery([
              {
                name: "placement_source",
                label: "Channel",
                operator: FilterTypesEnum.icontains,
                value: channelValue,
                index: 0,
              },
            ]);
            if (filterStr) {
              const params = new URLSearchParams(window.location.search);
              params.set("filters", filterStr);
              router.push(buildPathWithSearchParams("/influencers", params));
            }
          };
          const channelContent = (
            <div
              className={`rounded-lg py-1.5 px-1 lg:py-2 lg:px-2 block w-full font-semibold text-center text-white text-sm ${
                isInfluencerData
                  ? "cursor-pointer hover:opacity-90 transition-opacity"
                  : ""
              }`}
              style={{
                backgroundColor: mapChannelToColor(
                  CONSTANT_LABELS[channelValue] ?? channelValue,
                  channelColorMap
                ),
              }}
            >
              {channelName ?? capitalizeFirstLetter(channelValue)}
            </div>
          );
          return (
            <div className="w-full flex items-center relative">
              {isInfluencerData ? (
                <button
                  type="button"
                  onClick={handleNavigateToInfluencers}
                  className="w-full min-w-0 text-left"
                  title="View influencers in this channel"
                >
                  {channelContent}
                </button>
              ) : (
                channelContent
              )}

              {channelValue === "direct" ? (
                <div className={`absolute -right-5 top-1 lg:top-3`}>
                  <DirectTooltip />
                </div>
              ) : null}
              {channelValue === "organic" ? (
                <div className={`absolute -right-5 top-1 lg:top-3`}>
                  <OrganicTooltip />
                </div>
              ) : null}
            </div>
          );
        },
        footer: ({ table }) => {
          const pagination = table.getState().pagination;
          const rows: any[] = table
            .getRowModel()
            .rows.slice(
              pagination.pageIndex * pagination.pageSize,
              (pagination.pageIndex + 1) * pagination.pageSize
            );
          return (
            <div className="font-semibold flex items-center gap-1">
              <span> Total ({rows?.length ?? 0})</span>
            </div>
          );
        },
      }),
      columnHelper.accessor("historical", {
        enableHiding: true,
        enableResizing: false,
        size: selectedTimerange > 1 ? (isMobile ? 200 : 280) : 0,
        enableSorting: false,
        cell: (info) =>
          selectedTimerange > 1 ? (
            <HistoricalChartDisplay
              info={info as any}
              compared={compared}
              disabledMetrics={
                isInfluencerData
                  ? disabledInfluencerHistoricalChartMetrics
                  : undefined
              }
            />
          ) : null,
        header: () =>
          selectedTimerange > 1 ? (
            <HistoricalChartHeaderSelect
              disabledMetrics={
                isInfluencerData
                  ? disabledInfluencerHistoricalChartMetrics
                  : undefined
              }
            />
          ) : null,
      }),
      columnHelper.accessor("total_spend", {
        enableHiding: true,
        sortUndefined: -1,
        enableResizing: false,
        sortDescFirst: true,
        size: compared ? (isMobile ? 160 : 210) : isMobile ? 120 : 150,
        header: ({ header }) => (
          <MetricNameWithTooltip metric={header.id as any} />
        ),
        cell: (info) => <SpendTableDisplay info={info} compared={compared} />,
        footer: (info) => (
          <SpendTableDisplay
            compared={compared}
            total={data?.overall?.[nvrKey]?.total_spend ?? 0}
            comparedTotal={comparedData?.overall?.[nvrKey]?.total_spend ?? 0}
          />
        ),
      }),
      columnHelper.accessor("customer_purchase", {
        sortUndefined: -1,
        enableHiding: true,
        enableResizing: false,
        size: compared ? (isMobile ? 150 : 190) : isMobile ? 120 : 150,
        cell: (info) => <OrdersTableDisplay info={info} compared={compared} />,
        header: ({ header }) => (
          <MetricNameWithTooltip metric={header.id as any} />
        ),
        footer: (info) => (
          <OrdersTableDisplay
            compared={compared}
            total={data?.overall?.[nvrKey]?.customer_purchase ?? 0}
            comparedTotal={
              comparedData?.overall?.[nvrKey]?.customer_purchase ?? 0
            }
          />
        ),
      }),

      columnHelper.accessor("customer_revenue", {
        sortUndefined: -1,
        enableHiding: true,
        enableResizing: false,
        size: compared ? (isMobile ? 160 : 210) : isMobile ? 120 : 150,
        cell: (info) => <RevenueTableDisplay info={info} compared={compared} />,
        header: ({ header }) => (
          <MetricNameWithTooltip metric={header.id as any} />
        ),
        footer: (info) => (
          <RevenueTableDisplay
            compared={compared}
            total={data?.overall?.[nvrKey]?.customer_revenue ?? 0}
            comparedTotal={
              comparedData?.overall?.[nvrKey]?.customer_revenue ?? 0
            }
          />
        ),
      }),

      columnHelper.accessor("return_on_ad_spend", {
        enableHiding: true,
        enableResizing: false,
        sortUndefined: -1,
        size: compared ? (isMobile ? 140 : 180) : isMobile ? 120 : 150,
        cell: (info) => (
          <RoasTableDisplay
            info={info}
            compared={compared}
            channel={
              info.row.original.channel === "meta"
                ? "facebook"
                : (info.row.original.channel as any)
            }
          />
        ),
        header: ({ header }) => (
          <MetricNameWithTooltip metric={header.id as any} />
        ),
        footer: (info) => (
          <RoasTableDisplay
            compared={compared}
            total={data?.overall?.[nvrKey]?.return_on_ad_spend ?? 0}
            comparedTotal={
              comparedData?.overall?.[nvrKey]?.return_on_ad_spend ?? 0
            }
          />
        ),
      }),

      columnHelper.accessor("acquisition_return_on_ad_spend", {
        enableHiding: true,
        enableSorting: false,
        enableResizing: false,
        sortUndefined: -1,
        size: compared ? (isMobile ? 140 : 180) : isMobile ? 120 : 150,
        cell: (info) => (
          <RoasTableDisplay
            info={info}
            compared={compared}
            channel={
              info.row.original.channel === "meta"
                ? "facebook"
                : (info.row.original.channel as any)
            }
          />
        ),
        header: ({ header }) => (
          <MetricNameWithTooltip metric={header.id as any} />
        ),
        footer: () => (
          <RoasTableDisplay
            compared={compared}
            total={getAcquisitionRoas(data?.overall)}
            comparedTotal={getAcquisitionRoas(comparedData?.overall)}
          />
        ),
      }),

      columnHelper.accessor("customer_acquisition_cost", {
        enableHiding: true,
        size: compared ? (isMobile ? 140 : 180) : isMobile ? 120 : 150,
        enableResizing: false,
        sortUndefined: -1,
        cell: (info) => <CacTableDisplay info={info} compared={compared} />,
        header: ({ header }) => (
          <MetricNameWithTooltip metric={header.id as any} />
        ),
        footer: (info) => (
          <CacTableDisplay
            compared={compared}
            total={data?.overall?.[nvrKey]?.customer_acquisition_cost ?? 0}
            comparedTotal={
              comparedData?.overall?.[nvrKey]?.customer_acquisition_cost ?? 0
            }
          />
        ),
      }),
      columnHelper.accessor("new_customer_purchase_ratio", {
        enableHiding: true,
        sortUndefined: -1,

        enableResizing: false,
        size: compared ? (isMobile ? 140 : 180) : isMobile ? 120 : 180,
        cell: (info) => (
          <NewCustomerRateTableDisplay info={info} compared={compared} />
        ),
        header: ({ header }) => (
          <MetricNameWithTooltip metric={header.id as any} />
        ),
        footer: (info) => (
          <NewCustomerRateTableDisplay
            compared={compared}
            total={data?.overall?.[nvrKey]?.new_customer_purchase_ratio ?? 0}
            comparedTotal={
              comparedData?.overall?.[nvrKey]?.new_customer_purchase_ratio ?? 0
            }
          />
        ),
      }),
      columnHelper.accessor("new_visitor_pageview_ratio", {
        enableHiding: true,
        enableResizing: false,
        sortUndefined: -1,

        size: compared ? (isMobile ? 140 : 180) : isMobile ? 120 : 150,
        cell: (info) => (
          <NewVisitorRateTableDisplay info={info} compared={compared} />
        ),
        header: ({ header }) => (
          <MetricNameWithTooltip metric={header.id as any} />
        ),
        footer: (info) => (
          <NewVisitorRateTableDisplay
            compared={compared}
            total={data?.overall?.[nvrKey]?.new_visitor_pageview_ratio ?? 0}
            comparedTotal={
              comparedData?.overall?.[nvrKey]?.new_visitor_pageview_ratio ?? 0
            }
          />
        ),
      }),
      columnHelper.accessor("average_order_value", {
        enableHiding: true,
        enableResizing: false,
        sortUndefined: -1,
        size: compared ? (isMobile ? 140 : 180) : isMobile ? 120 : 150,
        cell: (info) => <AovTableDisplay info={info} compared={compared} />,
        header: ({ header }) => (
          <MetricNameWithTooltip metric={header.id as any} />
        ),
        footer: (info) => (
          <AovTableDisplay
            compared={compared}
            total={data?.overall?.[nvrKey]?.average_order_value ?? 0}
            comparedTotal={
              comparedData?.overall?.[nvrKey]?.average_order_value ?? 0
            }
          />
        ),
      }),
      columnHelper.accessor("conversion_rate", {
        enableHiding: true,
        enableResizing: false,
        sortUndefined: -1,
        size: compared ? (isMobile ? 140 : 180) : isMobile ? 120 : 150,
        cell: (info) => (
          <ConversionRateTableDisplay info={info} compared={compared} />
        ),
        header: ({ header }) => (
          <MetricNameWithTooltip metric={header.id as any} />
        ),
        footer: (info) => (
          <ConversionRateTableDisplay
            compared={compared}
            total={data?.overall?.[nvrKey]?.conversion_rate ?? 0}
            comparedTotal={
              comparedData?.overall?.[nvrKey]?.conversion_rate ?? 0
            }
          />
        ),
      }),
      columnHelper.accessor("cost_per_order", {
        enableHiding: true,
        enableResizing: false,
        sortUndefined: -1,
        size: compared ? (isMobile ? 140 : 180) : isMobile ? 120 : 150,
        cell: (info) => <CpoTableDisplay info={info} compared={compared} />,
        header: ({ header }) => (
          <MetricNameWithTooltip metric={header.id as any} />
        ),
        footer: (info) => (
          <CpoTableDisplay
            compared={compared}
            total={data?.overall?.[nvrKey]?.cost_per_order ?? 0}
            comparedTotal={comparedData?.overall?.[nvrKey]?.cost_per_order ?? 0}
          />
        ),
      }),
      columnHelper.accessor("cost_per_click", {
        enableHiding: true,
        enableResizing: false,
        sortUndefined: -1,
        size: compared ? (isMobile ? 140 : 180) : isMobile ? 120 : 150,
        cell: (info) => (
          <OptionalCurrencyTableDisplay
            value={info.getValue()}
            comparedValue={info.row.original.compared?.cost_per_click}
            compared={compared}
          />
        ),
        header: ({ header }) => (
          <MetricNameWithTooltip metric={header.id as any} />
        ),
        footer: () => (
          <OptionalCurrencyTableDisplay
            compared={compared}
            value={data?.overall?.fixed?.cost_per_click}
            comparedValue={comparedData?.overall?.fixed?.cost_per_click}
          />
        ),
      }),
      columnHelper.accessor("cost_per_view", {
        enableHiding: true,
        enableResizing: false,
        sortUndefined: -1,
        size: compared ? (isMobile ? 140 : 180) : isMobile ? 120 : 150,
        cell: (info) => (
          <OptionalCurrencyTableDisplay
            value={info.getValue()}
            comparedValue={info.row.original.compared?.cost_per_view}
            compared={compared}
          />
        ),
        header: ({ header }) => (
          <MetricNameWithTooltip metric={header.id as any} />
        ),
        footer: () => (
          <OptionalCurrencyTableDisplay
            compared={compared}
            value={getCostPerView(data?.overall?.fixed)}
            comparedValue={getCostPerView(comparedData?.overall?.fixed)}
          />
        ),
      }),
      columnHelper.accessor("customer_unique_addtocart", {
        enableHiding: true,
        enableResizing: false,
        sortUndefined: -1,

        size: compared ? (isMobile ? 150 : 200) : isMobile ? 120 : 150,
        cell: (info) => <AtcTableDisplay info={info} compared={compared} />,
        header: ({ header }) => (
          <MetricNameWithTooltip metric={header.id as any} />
        ),
        footer: (info) => (
          <AtcTableDisplay
            compared={compared}
            total={data?.overall?.[nvrKey]?.customer_unique_addtocart ?? 0}
            comparedTotal={
              comparedData?.overall?.[nvrKey]?.customer_unique_addtocart ?? 0
            }
          />
        ),
      }),
      columnHelper.accessor("customer_unique_productview", {
        enableSorting: true,
        enableHiding: true,
        enableResizing: false,
        sortUndefined: -1,

        size: compared ? (isMobile ? 150 : 200) : isMobile ? 120 : 150,
        cell: (info) => (
          <ProductviewTableDisplay info={info} compared={compared} />
        ),
        header: ({ header }) => (
          <MetricNameWithTooltip metric={header.id as any} />
        ),
        footer: (info) => (
          <ProductviewTableDisplay
            compared={compared}
            total={data?.overall?.[nvrKey]?.customer_unique_productview ?? 0}
            comparedTotal={
              comparedData?.overall?.[nvrKey]?.customer_unique_productview ?? 0
            }
          />
        ),
      }),
      columnHelper.accessor("customer_unique_pageview", {
        enableSorting: true,
        enableHiding: true,
        enableResizing: false,
        sortUndefined: -1,

        size: compared ? (isMobile ? 150 : 200) : isMobile ? 120 : 150,
        cell: (info) => (
          <PageviewTableDisplay info={info} compared={compared} />
        ),
        header: ({ header }) => (
          <MetricNameWithTooltip metric={header.id as any} />
        ),
        footer: (info) => (
          <PageviewTableDisplay
            compared={compared}
            total={data?.overall?.[nvrKey]?.customer_unique_pageview ?? 0}
            comparedTotal={
              comparedData?.overall?.[nvrKey]?.customer_unique_pageview ?? 0
            }
          />
        ),
      }),
      columnHelper.accessor("customer_addtocart", {
        enableHiding: true,
        enableResizing: false,
        sortUndefined: -1,

        size: compared ? (isMobile ? 150 : 200) : isMobile ? 120 : 150,
        cell: (info) => <AtcTableDisplay info={info} compared={compared} />,
        header: ({ header }) => (
          <MetricNameWithTooltip metric={header.id as any} />
        ),
        footer: (info) => (
          <AtcTableDisplay
            compared={compared}
            total={data?.overall?.[nvrKey]?.customer_addtocart ?? 0}
            comparedTotal={
              comparedData?.overall?.[nvrKey]?.customer_addtocart ?? 0
            }
          />
        ),
      }),
      columnHelper.accessor("customer_productview", {
        enableSorting: true,
        enableHiding: true,
        enableResizing: false,
        sortUndefined: -1,

        size: compared ? (isMobile ? 150 : 200) : isMobile ? 120 : 150,
        cell: (info) => (
          <ProductviewTableDisplay info={info} compared={compared} />
        ),
        header: ({ header }) => (
          <MetricNameWithTooltip metric={header.id as any} />
        ),
        footer: (info) => (
          <ProductviewTableDisplay
            compared={compared}
            total={data?.overall?.[nvrKey]?.customer_productview ?? 0}
            comparedTotal={
              comparedData?.overall?.[nvrKey]?.customer_productview ?? 0
            }
          />
        ),
      }),
      columnHelper.accessor("customer_pageview", {
        enableSorting: true,
        enableHiding: true,
        sortUndefined: -1,

        enableResizing: false,
        size: compared ? (isMobile ? 150 : 200) : isMobile ? 120 : 150,
        cell: (info) => (
          <PageviewTableDisplay info={info} compared={compared} />
        ),
        header: ({ header }) => (
          <MetricNameWithTooltip metric={header.id as any} />
        ),
        footer: (info) => (
          <PageviewTableDisplay
            compared={compared}
            total={data?.overall?.[nvrKey]?.customer_pageview ?? 0}
            comparedTotal={
              comparedData?.overall?.[nvrKey]?.customer_pageview ?? 0
            }
          />
        ),
      }),
    ],
    [
      compared,
      comparedData?.overall,
      data?.overall,
      nvrKey,
      selectedTimerange,
      isMobile,
      isInfluencerData,
      router,
      channelColorMap,
      channelNameMap,
    ]
  );

  let emptyState;
  let loadingState;
  if (loading) {
    loadingState = (
      <div className="relative flex w-full justify-center items-center py-8">
        <div className="loader mr-4"></div>
        <p className="text-foreground text-sm lg:text-base">Loading data...</p>
      </div>
    );
  } else if (!data) {
    emptyState =
      axios.isAxiosError(error) && error?.status === 504 ? (
        gatewayTimeoutMessage
      ) : (
        <div className="relative flex w-full justify-center items-center py-8 px-4">
          <p className="text-sm lg:text-base text-center">
            No data found for selected time-range...
          </p>
        </div>
      );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <div className="flex flex-row justify-between items-center gap-3 lg:gap-4">
          <CardTitle className="h3">
            {title
              ? title
              : isInfluencerData
                ? "Influencer Channel Overview"
                : "Channel Overview"}
          </CardTitle>
          <div className="flex flex-wrap justify-end gap-2">
            {activeView === "chart" ? (
              <>
                <Select
                  value={chartMetric}
                  onValueChange={(value) =>
                    setChartMetric(value as ChannelOverviewChartMetric)
                  }
                >
                  <SelectTrigger
                    variant="alternate"
                    className="w-full min-w-[190px] sm:w-auto font-semibold"
                    aria-label="Select chart metric"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {chartMetricOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {chartChannels.length > 0 ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="flex h-10 lg:h-12 min-w-[150px] font-semibold items-center justify-between whitespace-nowrap rounded-lg border border-input bg-background pl-3 pr-2 text-sm shadow-sm transition-all focus:outline-none focus:ring-1 focus:ring-ring gap-2"
                        aria-label="Select chart channels"
                      >
                        {hiddenChartChannelCount === 0
                          ? "All Channels"
                          : `${visibleChartChannels.length} / ${chartChannels.length} Channels`}
                        <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground opacity-50" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-56 p-2">
                      <div className="space-y-1">
                        <button
                          type="button"
                          onClick={toggleAllChartChannels}
                          className="flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-sm font-medium hover:bg-accent"
                        >
                          <span>
                            {allChartChannelsVisible
                              ? "Unselect all"
                              : "Select all"}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {visibleChartChannels.length}/{chartChannels.length}
                          </span>
                        </button>
                        <div className="my-1 h-px bg-border" />
                        {chartChannels.map((channel) => {
                          const label =
                            channelNameMap[channel] ??
                            CONSTANT_LABELS[channel] ??
                            capitalizeFirstLetter(channel);

                          return (
                            <label
                              key={channel}
                              className="flex items-center gap-2.5 px-2 py-1.5 rounded-sm hover:bg-accent cursor-pointer text-sm"
                            >
                              <Checkbox
                                checked={!hiddenChannels.has(channel)}
                                onCheckedChange={() =>
                                  toggleChartChannel(channel)
                                }
                              />
                              <span
                                className="w-2.5 h-2.5 rounded-sm shrink-0"
                                style={{
                                  backgroundColor: mapChannelToColor(
                                    CONSTANT_LABELS[channel] ?? channel,
                                    channelColorMap
                                  ),
                                }}
                              />
                              <span className="truncate">{label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                ) : null}
              </>
            ) : (
              <>
                <ChannelOverviewExportMenu
                  isInfluencerData={isInfluencerData}
                  filters={filters}
                  size="default"
                />
                <div className="mt-0 xl:mt-0">
                  <ColumnSelector
                    variant="alternate"
                    size="default"
                    table={
                      isInfluencerData
                        ? "influencerChannelOverview"
                        : "marketingChannelOverview"
                    }
                  />
                </div>
              </>
            )}

            {canShowChart ? (
              <div className="flex items-center rounded-lg border border-input bg-background h-10 lg:h-12 p-1">
                <button
                  type="button"
                  onClick={() => setView("chart")}
                  className={`flex items-center justify-center px-2.5 h-full rounded-md transition-colors ${
                    activeView === "chart"
                      ? "bg-accent text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  aria-label="Show chart view"
                  aria-pressed={activeView === "chart"}
                  title="Chart view"
                >
                  <BarChart2 className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setView("table")}
                  className={`flex items-center justify-center px-2.5 h-full rounded-md transition-colors ${
                    activeView === "table"
                      ? "bg-accent text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  aria-label="Show table view"
                  aria-pressed={activeView === "table"}
                  title="Table view"
                >
                  <Table2 className="w-4 h-4" />
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        {activeView === "chart" ? (
          loadingState ? (
            <MarketingChannelOverviewChartSkeleton />
          ) : (
            <MarketingChannelOverviewChart
              data={data}
              comparedData={comparedData}
              channels={visibleChartChannels}
              metric={chartMetric}
              granularity={granularity}
              compared={compared}
            />
          )
        ) : (
          <Table
            id="marketing-channel-overview-table"
            columns={columns}
            disableRowsPerPage={true}
            columnsOrder={selectedColumns}
            pinnedColumns={pinnedColumns}
            loadingState={loadingState}
            defaultSorting={DEFAULT_SORTING}
            showFooter
            emptyState={emptyState}
            visibleColumns={visibleColumns}
            data={tableData}
            enableResizing
            spacing={compared ? "wide" : "normal"}
          />
        )}
      </CardContent>
      {allChannelsSelected ? null : (
        <CardFooter>
          <p className="text-xs lg:text-sm px-1">
            The totals displayed can differ from your store, as you have not
            included all{" "}
            <a
              href="https://knowledge.tracify.ai/en/articles/22107-how-do-included-multi-channel-touchpoints-influence-your-attribution-data"
              target="_blank"
              className="underline"
            >
              channel touchpoints
            </a>{" "}
            in the attribution.
          </p>
        </CardFooter>
      )}
    </Card>
  );
}

export default MarketingChannelOverviewTable;
